import {
  ApiOutlined,
  DeploymentUnitOutlined,
  RobotOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { itemRouteSegment } from "@p0security/shared/install/routes";
import { Flex, Spacing, Tag, Timeline, Typography } from "@p0security/ui";
import { Identity } from "components/common/Identity";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import type { ReactNode } from "react";
import { useMemo } from "react";

import { useAgenticInstalls } from "./AgenticGateway";
import type {
  DelegateIdentity,
  GatewayHostedIdentity,
} from "./delegateIdentity";
import { identityOfDelegate, isGatewayHosted } from "./delegateIdentity";

/** One identity in a delegation chain — an issuer/subject pair. */
export type Delegate = { iss?: string | null; sub: string };

/** Orders a delegation chain: the delegating agents (immediate caller first),
 * then the subject they acted for — unless the innermost agent already is it. */
export const delegatesOf = (
  agents: ReadonlyArray<Delegate>,
  subject: string | undefined
): Delegate[] => [
  ...agents,
  ...(subject && subject !== agents.at(-1)?.sub
    ? [{ sub: subject, iss: null }]
    : []),
];

/** The identity a delegation chain was ultimately acted for — the chain's
 * last delegate, or undefined when fewer than two identities means nobody
 * acted for anybody else. */
export const subjectOf = (
  agents: ReadonlyArray<Delegate>,
  subject: string | undefined
): Delegate | undefined => {
  const delegates = delegatesOf(agents, subject);
  return delegates.length > 1 ? delegates[delegates.length - 1] : undefined;
};

type DelegateSubjectProps = { identity: DelegateIdentity; sub: string };

/** The install page of a gateway-hosted component. */
const installHrefOf = ({ id, type }: GatewayHostedIdentity) =>
  `integrations/agentic/${type}/${itemRouteSegment(id)}`;

const ClientSubject: React.FC<DelegateSubjectProps> = ({ sub }) => (
  <Identity value={{ role: "principal", sub, type: "agent" }} />
);

/** Links a gateway-hosted subject to its install, when one is installed. */
const GatewayHostedSubject: React.FC<DelegateSubjectProps> = ({
  identity,
  sub,
}) =>
  isGatewayHosted(identity) && identity.isInstalled ? (
    <TenantAwareLink to={installHrefOf(identity)}>{sub}</TenantAwareLink>
  ) : (
    <>{sub}</>
  );

const PlainSubject: React.FC<DelegateSubjectProps> = ({ sub }) => <>{sub}</>;

/** How a delegate reads: the icon and kind that annotate it, and how to show
 * its subject. An unrecognized delegate has no icon or kind. */
const DELEGATE_VIEWS: Record<
  DelegateIdentity["type"],
  { Subject: React.FC<DelegateSubjectProps>; icon?: ReactNode; kind?: string }
> = {
  "a2a-bridge": {
    Subject: GatewayHostedSubject,
    icon: <DeploymentUnitOutlined />,
    kind: "A2A bridge",
  },
  client: { Subject: ClientSubject, icon: <RobotOutlined />, kind: "Agent" },
  server: {
    Subject: GatewayHostedSubject,
    icon: <ApiOutlined />,
    kind: "MCP server",
  },
  user: { Subject: PlainSubject, icon: <UserOutlined />, kind: "User" },
  unknown: { Subject: PlainSubject },
};

/** The icon that marks a delegate's kind, for a spot too compact for
 * `DelegateNode`'s full kind label — an unrecognized delegate has none. */
export const delegateIconOf = (identity: DelegateIdentity): ReactNode =>
  DELEGATE_VIEWS[identity.type].icon;

/** Names a delegate's position in the chain; the middle of a chain is unnamed. */
const roleTitleOf = (index: number, count: number) => {
  if (index === 0) return "Actor";
  if (index === count - 1) return "Subject";
  return undefined;
};

/** Renders one delegate: its subject and position, then its kind. */
const DelegateNode: React.FC<{ delegate: Delegate; role?: string }> = ({
  delegate,
  role,
}) => {
  const installs = useAgenticInstalls();
  const identity = useMemo(
    () => identityOfDelegate(delegate, installs),
    [delegate, installs]
  );
  const { Subject, icon, kind } = DELEGATE_VIEWS[identity.type];
  return (
    <Flex vertical>
      <Flex align="center" gap={6}>
        <Subject identity={identity} sub={delegate.sub} />
        {role ? (
          <Spacing asChild m={0}>
            <Tag>{role}</Tag>
          </Spacing>
        ) : null}
      </Flex>

      {kind ? (
        <Typography.Text type="secondary">
          {icon} {kind}
        </Typography.Text>
      ) : null}
    </Flex>
  );
};

/** Renders a delegation chain as a timeline: the acting agent / client first
 * ("Actor"), the originating agent / client last ("Subject"). A chain of fewer
 * than two identities is nobody acting for anybody else, so it renders as
 * "(no delegation)" rather than a one-entry timeline. */
export const DelegationChain: React.FC<{ delegates: Delegate[] }> = ({
  delegates,
}) =>
  delegates.length < 2 ? (
    <Typography.Text data-testid="no-delegation" type="secondary">
      (no delegation)
    </Typography.Text>
  ) : (
    <Timeline
      className="[&_li.ant-timeline-item-last]:pb-0!"
      data-testid="delegation-chain"
      items={delegates.map((delegate, index) => ({
        children: (
          <DelegateNode
            delegate={delegate}
            role={roleTitleOf(index, delegates.length)}
          />
        ),
      }))}
    />
  );
